import * as React from 'react';
import { Loader2 } from 'lucide-react';
import { FormHandles, SubmitHandler } from '@unform/core';
import { Form } from '@unform/web';
import { Dialog, DialogContent } from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Select, Textarea } from 'components/Shared';
import { notify } from 'services/notifications';
import { useFetchViagensJustificationOptions } from 'hooks/api/viagens-controle';
import { useValidation } from 'hooks';
import type { SelectOption } from 'contracts/Common';
import * as Yup from 'yup';
import type { ViagemControle } from './types';

type FormData = { justificationId: number | string; comment: string };

type Props = {
  viagem: ViagemControle | null;
  loading?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: { justificationId: number; comment: string }) => void;
};

export const ViagemControleRejectDialog = ({
  viagem,
  loading = false,
  onOpenChange,
  onSubmit,
}: Props) => {
  const formRef = React.useRef<FormHandles>(null);
  const { handleFormErrors } = useValidation();
  const { fetchViagensJustificationOptions } =
    useFetchViagensJustificationOptions();
  const [justificationOptions, setJustificationOptions] = React.useState<
    SelectOption[]
  >([]);
  const [loadingJustifications, setLoadingJustifications] =
    React.useState(false);
  const open = Boolean(viagem);

  React.useEffect(() => {
    if (!open) return;

    setLoadingJustifications(true);
    fetchViagensJustificationOptions('estadia')
      .then((options) => {
        setJustificationOptions(
          options.map(({ id, description }) => ({ value: id, label: description }))
        );
      })
      .catch((error: any) => notify('error', error?.message))
      .finally(() => setLoadingJustifications(false));
  }, [fetchViagensJustificationOptions, open]);

  const handleSubmit = React.useCallback<SubmitHandler>(
    async (data: FormData) => {
      try {
        formRef.current?.setErrors({});

        const validData = await Yup.object({
          justificationId: Yup.number()
            .typeError('Selecione uma justificativa')
            .required('Selecione uma justificativa'),
          comment: Yup.string()
            .trim()
            .required('Informe o comentário'),
        }).validate(data, { abortEarly: false });

        onSubmit({
          justificationId: Number(validData.justificationId),
          comment: validData.comment,
        });
      } catch (error) {
        handleFormErrors(error, formRef);
      }
    },
    [handleFormErrors, onSubmit]
  );

  const handleClose = React.useCallback(() => {
    if (loading) return;
    formRef.current?.reset();
    onOpenChange(false);
  }, [loading, onOpenChange]);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => !nextOpen && handleClose()}
    >
      <DialogContent className="max-w-[550px] bg-white p-6 sm:rounded-lg">
        <h2 className="font-GilroyBold text-xl text-slate-900">
          Adicionar justificativa
        </h2>
        <Form ref={formRef} onSubmit={handleSubmit}>
          <div className="flex flex-col gap-5">
            <Select
              name="justificationId"
              label="Justificativa"
              placeholder="Selecione"
              options={justificationOptions}
              isLoading={loadingJustifications}
              isDisabled={loading}
              classContainer="min-h-[72px]"
            />
            <Textarea
              name="comment"
              label="Comentário"
              placeholder="Digite o comentário"
              rows={4}
              disabled={loading}
              classContainer="min-h-[160px]"
            />
            <div className="mt-2 flex gap-4">
              <Button
                type="button"
                variant="secondary"
                className="h-10 flex-1 bg-blue-50 font-GilroySemibold text-slate-900 hover:bg-blue-100"
                disabled={loading}
                onClick={handleClose}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="h-10 flex-1 bg-black font-GilroySemibold text-white hover:bg-slate-800"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Salvar'
                )}
              </Button>
            </div>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
